import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Link, useParams } from 'react-router-dom';
import StudentNav from '../components/shared/StudentNav';
import { claimPendingLessonsForUser } from '../utils/studentLessonAccess';
import {
  buildStudentLessonDetailViewModel,
  getStudentLessonAccountStatus,
} from '../utils/studentLessons';

function formatLabel(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function StudentLessonDetailPage() {
  const { currentUser, userData } = useAuth();
  const { lessonId } = useParams();
  const [viewModel, setViewModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadLesson = async () => {
      if (!currentUser?.uid || !lessonId) return;

      setLoading(true);
      setError('');
      try {
        await claimPendingLessonsForUser(db, currentUser, {
          studentName: userData?.name || '',
        });

        const lessonSnap = await getDoc(doc(db, 'lessonInstances', lessonId));
        if (!lessonSnap.exists()) {
          setError('We could not find that lesson.');
          setViewModel(null);
          return;
        }

        const lesson = { lessonId: lessonSnap.id, ...lessonSnap.data() };
        const accountStatus = getStudentLessonAccountStatus(lesson, currentUser);
        if (accountStatus !== 'linked' && accountStatus !== 'pending_invite') {
          setError('This lesson is not available to your account.');
          setViewModel(null);
          return;
        }

        let instructorName = '';
        if (lesson.instructorId) {
          const instructorSnap = await getDoc(doc(db, 'users', lesson.instructorId)).catch(() => null);
          instructorName = instructorSnap?.exists() ? instructorSnap.data()?.name || '' : '';
        }

        setViewModel(
          buildStudentLessonDetailViewModel(
            { ...lesson, studentAccountStatus: accountStatus },
            { instructorName }
          )
        );
      } catch (loadIssue) {
        console.error('Error loading lesson detail:', loadIssue);
        setError('This lesson could not be loaded safely.');
        setViewModel(null);
      } finally {
        setLoading(false);
      }
    };

    loadLesson();
  }, [currentUser, lessonId, userData?.name]);

  return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8' }}>
      <StudentNav activeTab="lessons" />
      <div className="max-w-5xl mx-auto p-4">

      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Lesson Detail</p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-950">Lesson</h1>
          </div>
          <Link
            to="/lessons"
            className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Back to Lessons
          </Link>
        </div>

        {loading ? (
          <p className="mt-6 text-sm text-slate-500">Loading lesson...</p>
        ) : error ? (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
            {error}
          </div>
        ) : viewModel ? (
          <>
            <div className="mt-6 grid gap-4 md:grid-cols-4">
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sport</p>
                <p className="mt-2 text-sm font-medium text-slate-900">{viewModel.sportLabel}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Target Skill</p>
                <p className="mt-2 text-sm font-medium text-slate-900">{viewModel.targetSkillName}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Track</p>
                <p className="mt-2 text-sm font-medium text-slate-900">{viewModel.trackName}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</p>
                <p className="mt-2 text-sm font-medium text-slate-900">{viewModel.statusLabel}</p>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Why This Lesson</p>
              <p className="mt-2 text-sm text-slate-700">{viewModel.recommendationReason}</p>
              <p className="mt-3 text-sm text-slate-500">Instructor: {viewModel.instructorName}</p>
              <p className="mt-1 text-sm text-slate-500">{viewModel.dateLabel}</p>
              {viewModel.hasLinkedVideo ? (
                <div className="mt-2">
                  <p className="text-sm text-slate-500">Linked video review available for this lesson.</p>
                  <Link
                    to={`/reviews/${viewModel.linkedVideoId}`}
                    className="mt-2 inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Open linked review
                  </Link>
                </div>
              ) : (
                <p className="mt-1 text-sm text-slate-500">This lesson was created without a linked video.</p>
              )}
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="text-lg font-semibold text-slate-950">{viewModel.targetSkillName}</h2>
              <p className="mt-2 text-sm text-slate-600">{viewModel.targetSkillDescription}</p>
              {viewModel.nextSkillNames.length > 0 ? (
                <p className="mt-3 text-sm text-slate-600">
                  What comes next: {viewModel.nextSkillNames.join(', ')}
                </p>
              ) : null}
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="text-lg font-semibold text-slate-950">Lesson Stages</h2>
              {viewModel.stages.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500">{viewModel.lessonFlowEmptyText}</p>
              ) : (
                <div className="mt-4 space-y-4">
                  {viewModel.stages.map((stage) => (
                    <div key={stage.key} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{stage.title}</p>
                          <p className="mt-2 text-sm text-slate-700">
                            Status: {formatLabel(stage.completionStatus)}
                          </p>
                        </div>
                      </div>
                      {stage.drillNames.length > 0 ? (
                        <ul className="mt-3 space-y-1 text-sm text-slate-700">
                          {stage.drillNames.map((drillName) => (
                            <li key={drillName}>{drillName}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-3 text-sm text-slate-500">No drills were attached to this stage.</p>
                      )}
                      {stage.instructorNotes ? (
                        <p className="mt-3 text-sm text-slate-600">{stage.instructorNotes}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="text-lg font-semibold text-slate-950">Lesson Outcome</h2>
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                <div className="rounded-2xl bg-slate-50 px-4 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Outcome</p>
                  <p className="mt-2 text-sm font-medium text-slate-900">{formatLabel(viewModel.lessonOutcome.status)}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 px-4 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Skill Confidence</p>
                  <p className="mt-2 text-sm font-medium text-slate-900">{formatLabel(viewModel.lessonOutcome.skillConfidence)}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 px-4 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recommended Next Skill</p>
                  <p className="mt-2 text-sm font-medium text-slate-900">
                    {viewModel.lessonOutcome.recommendedNextSkillName || 'Stay with the current recommendation'}
                  </p>
                </div>
              </div>
              {viewModel.lessonOutcome.instructorSummary ? (
                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700">
                  {viewModel.lessonOutcome.instructorSummary}
                </div>
              ) : (
                <p className="mt-4 text-sm text-slate-500">No instructor summary was saved for this lesson.</p>
              )}
            </div>
          </>
        ) : null}
      </section>
      </div>
    </div>
  );
}

export default StudentLessonDetailPage;
